import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import type { Dispatch } from "@/lib/types";

// ============================================================================
// useDispatch — fetch a single dispatch by ID for the BUILD-15 detail view.
//
// v_route_data has one row per (dispatch, tech_empno, counter) tuple, so a
// multi-tech dispatch produces multiple rows. For the detail view we want:
//   - the dispatch-level fields (notes, address, customer, window, etc.)
//     from the first row (they're duplicated across all rows of one job)
//   - the per-tech assignment list (each tech, their status, their estimate)
//     extracted out into a separate array
//
// Returns null when no row exists for the given dispatch ID.
//
// Realtime: caller wires useRealtimeRefresh against mirror_dispatch +
// mirror_disptech so this query refetches automatically when a tech marks
// status from the field, when a dispatcher reassigns, etc.
// ============================================================================

export interface DispatchTechAssignment {
  tech_empno: string;
  tech_first: string | null;
  tech_last: string | null;
  tech_name: string | null;
  tech_dept: string | null;
  status: string | null;
  tech_est_hours: number | null;
  counter: number | null;
  /** Per-BLOCK date — a multi-day install carries one row per tech per day,
   *  so the same tech appears many times with different dates (Seth
   *  2026-06-11: consolidate per tech, show the dates + time on the job). */
  appointment_date: string;
}

export interface DispatchDetailRecord {
  // --- Dispatch-level (taken from the first row of v_route_data) ---
  dispatch: string;
  appointment_date: string;
  tpromtime: string | null;
  window_code: string | null;
  notes: string | null;
  dispatch_est_min: number | null;
  dispatch_priority: string | null;
  multitech: string | null;
  // Customer + property
  custno: string;
  customer_first: string | null;
  customer_last: string | null;
  customer_fullname: string | null;
  customer_phone: string | null;
  locno: string | null;
  locname: string | null;
  service_address: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  loc_latitude: number | null;
  loc_longitude: number | null;
  // --- Tech assignments (one per row in v_route_data) ---
  techs: DispatchTechAssignment[];
}

/** One entry per TECH with their blocks rolled up (Seth 2026-06-11: "I don't
 *  like the same techs listed so many times" — a 12-day install showed 12
 *  rows). Name + the dates they were on the job + total estimated hours. */
export interface TechGroup {
  tech_empno: string;
  name: string;
  dept: string | null;
  blocks: DispatchTechAssignment[];
  /** Unique calendar days on the job. */
  days: string[];
  firstDate: string;
  lastDate: string;
  totalEstHours: number;
  /** Status of the latest block — the tech's most recent state on this job. */
  latestStatus: string | null;
}

export function groupTechAssignments(techs: DispatchTechAssignment[]): TechGroup[] {
  const map = new Map<string, DispatchTechAssignment[]>();
  for (const t of techs) {
    const list = map.get(t.tech_empno);
    if (list) list.push(t);
    else map.set(t.tech_empno, [t]);
  }
  const groups: TechGroup[] = [...map.entries()].map(([empno, blocks]) => {
    const sorted = [...blocks].sort((a, b) =>
      (a.appointment_date ?? "").localeCompare(b.appointment_date ?? ""),
    );
    const days = [...new Set(sorted.map((b) => b.appointment_date).filter(Boolean))];
    const first = sorted[0];
    return {
      tech_empno: empno,
      name:
        first.tech_name ??
        (`${first.tech_first ?? ""} ${first.tech_last ?? ""}`.trim() || `Tech ${empno}`),
      dept: first.tech_dept,
      blocks: sorted,
      days,
      firstDate: days[0] ?? "",
      lastDate: days[days.length - 1] ?? "",
      totalEstHours: sorted.reduce((s, b) => s + (b.tech_est_hours ?? 0), 0),
      latestStatus: sorted[sorted.length - 1]?.status ?? null,
    };
  });
  groups.sort(
    (a, b) => a.firstDate.localeCompare(b.firstDate) || a.name.localeCompare(b.name),
  );
  return groups;
}

export function useDispatch(dispatchId: string | undefined) {
  return useQuery({
    queryKey: ["dispatch_detail", dispatchId],
    queryFn: async (): Promise<DispatchDetailRecord | null> => {
      if (!dispatchId) return null;

      // v_dispatch_full = v_route_data without the dept='03' filter, so the
      // detail view can open ANY dispatch (install/plumbing/sales), not just
      // service. The Board still uses the service-only v_route_data.
      const { data, error } = await supabase
        .from("v_dispatch_full")
        .select(
          "dispatch, tech_empno, counter, appointment_date, tpromtime, " +
            "window_code, status, tech_est_hours, notes, dispatch_est_min, " +
            "multitech, dispatch_priority, custno, customer_first, " +
            "customer_last, customer_fullname, customer_phone, locno, " +
            "locname, service_address, city, state, zip, loc_latitude, " +
            "loc_longitude, tech_first, tech_last, tech_name, tech_dept",
        )
        .eq("dispatch", dispatchId)
        .order("counter", { ascending: true, nullsFirst: false });

      if (error) throw error;
      if (!data || data.length === 0) return null;

      const rows = data as unknown as (Dispatch & {
        loc_latitude: number | null;
        loc_longitude: number | null;
      })[];
      const first = rows[0];

      const techs: DispatchTechAssignment[] = rows.map((r) => ({
        tech_empno: r.tech_empno,
        tech_first: r.tech_first,
        tech_last: r.tech_last,
        tech_name: r.tech_name,
        tech_dept: r.tech_dept,
        status: r.status,
        tech_est_hours: r.tech_est_hours,
        counter: r.counter,
        appointment_date: r.appointment_date,
      }));

      return {
        dispatch: first.dispatch,
        appointment_date: first.appointment_date,
        tpromtime: first.tpromtime,
        window_code: first.window_code,
        notes: first.notes,
        dispatch_est_min: first.dispatch_est_min,
        dispatch_priority: first.dispatch_priority,
        multitech: first.multitech,
        custno: first.custno,
        customer_first: first.customer_first,
        customer_last: first.customer_last,
        customer_fullname: first.customer_fullname,
        customer_phone: first.customer_phone,
        locno: first.locno,
        locname: first.locname,
        service_address: first.service_address,
        city: first.city,
        state: first.state,
        zip: first.zip,
        loc_latitude: first.loc_latitude,
        loc_longitude: first.loc_longitude,
        techs,
      };
    },
    enabled: !!dispatchId,
    staleTime: 30 * 1000,
  });
}
